import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

const GAP = 16;
const CARD_HEIGHT = 300;

function useVisible() {
  const [visible, setVisible] = useState(3);
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < 640) setVisible(1);
      else if (w < 1024) setVisible(2);
      else setVisible(3);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return visible;
}

export default function OurWorkCarousal({slides}) {
  const visible = useVisible();
  const total = slides.length;
  
  // Create infinite array
  const infiniteSlides = [...slides.slice(-visible), ...slides, ...slides.slice(0, visible)];
  const [currentIndex, setCurrentIndex] = useState(visible);
  const [isAnimating, setIsAnimating] = useState(false);
  const [cardWidth, setCardWidth] = useState(280);
  const containerRef = useRef(null);
  const containerInnerRef = useRef(null);
  const isLoopingRef = useRef(false); // Prevent multiple loops

  // Calculate card width
  useEffect(() => {
    const calculateWidth = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const totalGap = GAP * (visible - 1);
      const availableWidth = containerWidth - totalGap;
      const newCardWidth = availableWidth / visible;
      setCardWidth(Math.max(newCardWidth, 140));
    };
    
    calculateWidth();
    window.addEventListener("resize", calculateWidth);
    return () => window.removeEventListener("resize", calculateWidth);
  }, [visible]);

  // Reset index when visible changes
  useEffect(() => {
    setCurrentIndex(visible);
  }, [visible]);

  const step = cardWidth + GAP;

  const handleTransitionEnd = () => {
    setIsAnimating(false);
    
    // Loop back to start (when reached the end)
    if (currentIndex >= total + visible && !isLoopingRef.current) {
      isLoopingRef.current = true;
      // Disable transition temporarily
      if (containerInnerRef.current) {
        containerInnerRef.current.style.transition = "none";
      }
      setCurrentIndex(visible);
    }
    // Loop back to end (when reached the beginning)
    else if (currentIndex < visible && !isLoopingRef.current) {
      isLoopingRef.current = true;
      // Disable transition temporarily
      if (containerInnerRef.current) {
        containerInnerRef.current.style.transition = "none";
      }
      setCurrentIndex(total + visible - 1);
    }
  };

  // Apply transform after index changes without transition
  useEffect(() => {
    if (!containerInnerRef.current) return;
    
    if (isLoopingRef.current) {
      // Apply new position without transition
      containerInnerRef.current.style.transform = `translateX(-${currentIndex * step}px)`;
      // Force reflow
      void containerInnerRef.current.offsetHeight;
      // Re-enable transition
      containerInnerRef.current.style.transition = "transform 0.42s cubic-bezier(0.4, 0, 0.2, 1)";
      isLoopingRef.current = false;
    } else {
      containerInnerRef.current.style.transform = `translateX(-${currentIndex * step}px)`;
    }
  }, [currentIndex, step]);

  const goPrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(prev => prev - 1);
  };

  const goNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(prev => prev + 1);
  };

  const goToSlide = (index) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(index + visible);
  };

  const realIndex = ((currentIndex - visible) % total + total) % total;

  return (
    <div style={{ padding: "4rem 0 8rem 0" }} className="customcontainer">
      <div style={{ display: "flex", alignItems: "center", gap: 10,  margin: "0 auto",}}>
        {/* Left Button */}
        <motion.button
          onClick={goPrev}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          style={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            border: "1px solid #ddd",
            background: "white",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            flexShrink: 0,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--Background-light-Color)" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </motion.button>

        {/* Carousel Track */}
        <div ref={containerRef} style={{ flex: 1, overflow: "hidden" }}>
          <div
            ref={containerInnerRef}
            style={{
              display: "flex",
              gap: GAP,
              transform: `translateX(-${currentIndex * step}px)`,
              transition: isAnimating ? "transform 0.42s cubic-bezier(0.4, 0, 0.2, 1)" : "none",
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            {infiniteSlides.map((slide, i) => {
              const isVisible = Math.abs(i - currentIndex) < visible;
              return (
                <motion.div
                  key={i}
                  style={{
                    minWidth: cardWidth,
                    width: cardWidth,
                    height: CARD_HEIGHT,
                    borderRadius: 16,
                    overflow: "hidden",
                    flexShrink: 0,
                    cursor: "pointer",
                  }}
                  animate={{
                    scale: isVisible ? 1 : 0.85,
                    opacity: isVisible ? 1 : 0.5,
                  }}
                  transition={{ duration: 0.3 }}
                  onClick={() => goToSlide(i - visible)}
                >
                  <img
                    src={slide.url}
                    alt={slide.label}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    loading="lazy"
                  />
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Right Button */}
        <motion.button
          onClick={goNext}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          style={{
            width: 42,
            height: 42,
            borderRadius: "50%",
             border: "1px solid #ddd",
            background: "white",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            flexShrink: 0,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--Background-light-Color)" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </motion.button>
      </div>

      {/* Dots */}
      <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 24 }}>
        {slides.map((_, i) => (
          <motion.div
            key={i}
            onClick={() => goToSlide(i)}
            animate={{
              width: i === realIndex ? 24 : 8,
              backgroundColor: i === realIndex ? "var(--Background-light-Color)" : "#ccc",
            }}
            transition={{ duration: 0.2 }}
            style={{
              height: 8,
              borderRadius: 4,
              cursor: "pointer",
            }}
          />
        ))}
      </div>
    </div>
  );
}